import { supabase } from "@/config/supabase";
import { Service, ServiceInput, Package, Addon } from "@/types";
import { SUPABASE_TABLES } from "@/config/constants";

// Service columns - includes all fields needed for display and admin
const SERVICE_COLUMNS =
  "id,title,name,slug,description,shortDescription,basePrice,estimatedDuration,thumbnail,icon,pricing,featured,active,images,features,deliverables,includedItems,order,created_at,updated_at";
const PACKAGE_COLUMNS =
  "id,serviceId,title,description,basePrice,features,estimatedDeliveryDays,includedAddons,optionalAddons,enabled,popular,recommended,badge,badgeText,originalPrice,discount,validUntil,seasonal,seasonalTheme,order";
const ADDON_COLUMNS =
  "id,serviceId,title,description,price,category,recommended,popularWith,enabled,maxQuantity";

const withTimeout = <T>(
  promise: PromiseLike<T>,
  ms: number,
  timeoutMessage: string
): Promise<T> => {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(timeoutMessage)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
};

// Services
export const getAllServices = async (): Promise<Service[]> => {
  const { data, error } = await supabase
    .from(SUPABASE_TABLES.SERVICES)
    .select(SERVICE_COLUMNS)
    .eq("active", true)
    .order("order", { ascending: true });
  if (error || !data) return [];
  return data as Service[];
};

// Get all services including inactive (for admin)
export const getAllServicesAdmin = async (): Promise<Service[]> => {
  const { data, error } = await supabase
    .from(SUPABASE_TABLES.SERVICES)
    .select(SERVICE_COLUMNS)
    .order("order", { ascending: true });
  if (error || !data) return [];
  return data as Service[];
};

export const getServiceById = async (id: string): Promise<Service | null> => {
  const { data, error } = await supabase
    .from(SUPABASE_TABLES.SERVICES)
    .select(SERVICE_COLUMNS)
    .eq("id", id)
    .single();
  if (error || !data) return null;
  return data as Service;
};

export const createService = async (service: ServiceInput): Promise<string> => {
  // Prepare the service data - DB trigger will auto-generate slug, name, shortDescription
  const serviceData = {
    title: service.title,
    description: service.description,
    thumbnail: service.thumbnail || null,
    icon: service.icon || "📷",
    pricing: service.pricing || "Contact for Quote",
    featured: service.featured ?? false,
    active: service.active ?? true,
    basePrice: service.basePrice ?? 0,
    estimatedDuration: service.estimatedDuration || "Varies",
    features: service.features || [],
    deliverables: service.deliverables || [],
    order: service.order ?? 0,
  };

  const request = supabase.from(SUPABASE_TABLES.SERVICES).insert(serviceData).select("id").single();

  const response = await withTimeout(
    request,
    15000,
    "Create service timed out. Make sure Supabase is running and reachable."
  );
  const { data, error } = response as any;
  if (error) throw new Error(error.message);
  return (data as any).id as string;
};

export const updateService = async (id: string, updates: Partial<ServiceInput>): Promise<void> => {
  const { error } = await supabase.from(SUPABASE_TABLES.SERVICES).update(updates).eq("id", id);
  if (error) throw new Error(error.message);
};

export const deleteService = async (id: string): Promise<void> => {
  const { error } = await supabase.from(SUPABASE_TABLES.SERVICES).delete().eq("id", id);
  if (error) throw new Error(error.message);
};

// Packages
export const getAllPackages = async (): Promise<Package[]> => {
  const { data, error } = await supabase
    .from(SUPABASE_TABLES.PACKAGES)
    .select(PACKAGE_COLUMNS)
    .order("order", { ascending: true });
  if (error || !data) return [];
  return data as Package[];
};

export const getPackagesByServiceId = async (serviceId: string): Promise<Package[]> => {
  const { data, error } = await supabase
    .from(SUPABASE_TABLES.PACKAGES)
    .select(PACKAGE_COLUMNS)
    .eq("serviceId", serviceId)
    .order("order", { ascending: true });
  if (error || !data) return [];
  return data as Package[];
};

export const getPackageById = async (id: string): Promise<Package | null> => {
  const { data, error } = await supabase
    .from(SUPABASE_TABLES.PACKAGES)
    .select(PACKAGE_COLUMNS)
    .eq("id", id)
    .single();
  if (error || !data) return null;
  return data as Package;
};

export const createPackage = async (pkg: Omit<Package, "id">): Promise<string> => {
  const { data, error } = await supabase
    .from(SUPABASE_TABLES.PACKAGES)
    .insert(pkg)
    .select("id")
    .single();
  if (error) throw new Error(error.message);
  return (data as any).id as string;
};

export const updatePackage = async (id: string, updates: Partial<Package>): Promise<void> => {
  const { error } = await supabase.from(SUPABASE_TABLES.PACKAGES).update(updates).eq("id", id);
  if (error) throw new Error(error.message);
};

export const deletePackage = async (id: string): Promise<void> => {
  const { error } = await supabase.from(SUPABASE_TABLES.PACKAGES).delete().eq("id", id);
  if (error) throw new Error(error.message);
};

// Addons
export const getAllAddons = async (): Promise<Addon[]> => {
  const { data, error } = await supabase.from(SUPABASE_TABLES.ADDONS).select(ADDON_COLUMNS);
  if (error || !data) return [];
  return data as Addon[];
};

export const getAddonsByServiceId = async (serviceId: string): Promise<Addon[]> => {
  const { data, error } = await supabase
    .from(SUPABASE_TABLES.ADDONS)
    .select(ADDON_COLUMNS)
    .eq("serviceId", serviceId);
  if (error || !data) return [];
  return data as Addon[];
};

export const createAddon = async (addon: Omit<Addon, "id">): Promise<string> => {
  const { data, error } = await supabase
    .from(SUPABASE_TABLES.ADDONS)
    .insert(addon)
    .select("id")
    .single();
  if (error) throw new Error(error.message);
  return (data as any).id as string;
};

export const updateAddon = async (id: string, updates: Partial<Addon>): Promise<void> => {
  const { error } = await supabase.from(SUPABASE_TABLES.ADDONS).update(updates).eq("id", id);
  if (error) throw new Error(error.message);
};

export const deleteAddon = async (id: string): Promise<void> => {
  const { error } = await supabase.from(SUPABASE_TABLES.ADDONS).delete().eq("id", id);
  if (error) throw new Error(error.message);
};
